/**
 * @module AdapterConfigForm
 * @package @geenius/adapters/solidjs-solidui
 * @description Renders the SolidUI adapter configuration form for the SolidJS
 * SolidUI variant. It provides provider selection and credential inputs while
 * delegating persistence to the host application.
 */

import type { AdapterDomain, DomainAdapterConfig } from "@geenius/adapters";
import { DOMAIN_LABELS, getProvidersForDomain } from "@geenius/adapters";
import type { JSX } from "solid-js";
import { createEffect, createSignal, For, Show } from "solid-js";
import { cx } from "../lib/cx";

/**
 * Props accepted by the SolidUI adapter configuration form.
 *
 * @property domain Adapter domain being configured in the current dialog
 * @property initialConfig Existing configuration values used to hydrate the form
 * @property onSave Async or sync handler that persists the submitted configuration
 * @property onCancel Optional callback invoked when the user dismisses the form
 * @property class Optional class merged onto the form root
 * @property titleId Optional identifier applied to the form title for dialog labelling
 */
export interface AdapterConfigFormProps {
  domain: AdapterDomain;
  initialConfig?: DomainAdapterConfig;
  onSave: (config: DomainAdapterConfig) => Promise<void> | void;
  onCancel?: () => void;
  class?: string;
  titleId?: string;
}

/**
 * Renders the adapter configuration workflow for a single adapter domain.
 *
 * @param props Component props controlling the selected domain and form callbacks
 * @returns A provider chooser and credential form styled with SolidUI tokens
 */
export function AdapterConfigForm(props: AdapterConfigFormProps): JSX.Element {
  const providers = getProvidersForDomain(props.domain);
  const [provider, setProvider] = createSignal(
    props.initialConfig?.provider ?? providers[0]?.id ?? "",
  );
  const [apiKey, setApiKey] = createSignal(props.initialConfig?.apiKey ?? "");
  const [baseUrl, setBaseUrl] = createSignal(
    props.initialConfig?.baseUrl ?? "",
  );
  const [isSaving, setIsSaving] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);
  const selectedMeta = () => providers.find((entry) => entry.id === provider());

  const needsApiKey = () => selectedMeta()?.requiresApiKey ?? false;
  const needsBaseUrl = () => selectedMeta()?.requiresBaseUrl ?? false;

  createEffect(() => {
    if (!needsApiKey() && apiKey()) {
      setApiKey("");
    }
    if (!needsBaseUrl() && baseUrl()) {
      setBaseUrl("");
    }
  });

  const handleSubmit = async (event: Event): Promise<void> => {
    event.preventDefault();
    setError(null);

    if (!provider()) {
      setError("Select a provider");
      return;
    }

    if (needsApiKey() && !apiKey().trim()) {
      setError("API key is required");
      return;
    }

    setIsSaving(true);
    try {
      const config: DomainAdapterConfig = { provider: provider() };
      if (needsApiKey() && apiKey().trim()) {
        config.apiKey = apiKey().trim();
      }
      if (needsBaseUrl() && baseUrl().trim()) {
        config.baseUrl = baseUrl().trim();
      }
      await props.onSave(config);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      class={cx(
        "solidui-adapter-form space-y-6 rounded-md border border-border bg-card p-6 text-card-foreground shadow-sm",
        props.class,
      )}
      noValidate
    >
      <div>
        <h3 id={props.titleId} class="text-lg font-bold text-foreground">
          Configure {DOMAIN_LABELS[props.domain]}
        </h3>
        <p class="mt-1 text-sm text-muted-foreground">
          Select and configure your preferred provider.
        </p>
      </div>

      <Show when={error()}>
        <div
          class="rounded-md border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive"
          role="alert"
        >
          {error()}
        </div>
      </Show>

      <div class="space-y-3">
        <span class="block text-xs font-semibold uppercase text-muted-foreground">
          Provider
        </span>
        <div class="grid gap-2 sm:grid-cols-2">
          <For each={providers}>
            {(providerMeta) => (
              <button
                type="button"
                onClick={() => setProvider(providerMeta.id)}
                aria-pressed={provider() === providerMeta.id}
                class={cx(
                  "flex items-start gap-4 rounded-md border p-4 text-left transition-colors duration-200",
                  provider() === providerMeta.id
                    ? "border-primary/30 bg-primary/10 shadow-sm"
                    : "border-border bg-muted/20 hover:border-primary/20 hover:bg-accent/5",
                )}
              >
                <div class="flex-1">
                  <span class="block text-sm font-bold text-foreground">
                    {providerMeta.name}
                  </span>
                  <span class="mt-1 block text-xs leading-relaxed text-muted-foreground">
                    {providerMeta.description}
                  </span>
                  <span
                    class={cx(
                      "mt-2 inline-flex rounded px-2 py-0.5 text-[0.5rem] font-bold uppercase",
                      providerMeta.tier === "pronto"
                        ? "bg-primary/10 text-primary"
                        : providerMeta.tier === "lancio"
                          ? "bg-accent/10 text-accent"
                          : "bg-muted text-muted-foreground",
                    )}
                  >
                    {providerMeta.tier}
                  </span>
                </div>
              </button>
            )}
          </For>
        </div>
      </div>

      <Show when={needsApiKey()}>
        <div class="space-y-1.5">
          <label
            class="block text-xs font-semibold uppercase text-muted-foreground"
            for={`solidui-adapter-${props.domain}-apiKey`}
          >
            API Key
          </label>
          <input
            id={`solidui-adapter-${props.domain}-apiKey`}
            type="password"
            value={apiKey()}
            onInput={(event) => setApiKey(event.currentTarget.value)}
            placeholder={`Enter ${selectedMeta()?.name ?? provider() ?? "provider"} API key`}
            autocomplete="off"
            class="w-full rounded-md border border-input bg-background px-4 py-3 text-sm text-foreground shadow-sm outline-none transition-colors placeholder:text-muted-foreground focus:border-primary focus:ring-2 focus:ring-primary/20"
          />
        </div>
      </Show>

      <Show when={needsBaseUrl()}>
        <div class="space-y-1.5">
          <label
            class="block text-xs font-semibold uppercase text-muted-foreground"
            for={`solidui-adapter-${props.domain}-url`}
          >
            Base URL
          </label>
          <input
            id={`solidui-adapter-${props.domain}-url`}
            type="url"
            value={baseUrl()}
            onInput={(event) => setBaseUrl(event.currentTarget.value)}
            placeholder="https://..."
            class="w-full rounded-md border border-input bg-background px-4 py-3 text-sm text-foreground shadow-sm outline-none transition-colors placeholder:text-muted-foreground focus:border-primary focus:ring-2 focus:ring-primary/20"
          />
        </div>
      </Show>

      <div class="flex justify-end gap-3 pt-2">
        <Show when={props.onCancel}>
          <button
            type="button"
            onClick={() => props.onCancel?.()}
            class="rounded-md px-5 py-2.5 text-sm font-semibold text-muted-foreground transition-colors hover:text-foreground"
          >
            Cancel
          </button>
        </Show>
        <button
          type="submit"
          disabled={isSaving() || !provider()}
          class="inline-flex items-center justify-center rounded-md border border-primary/30 bg-primary px-8 py-3 text-sm font-bold text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 disabled:pointer-events-none disabled:opacity-50"
        >
          {isSaving() ? "Saving..." : "Save Configuration"}
        </button>
      </div>
    </form>
  );
}
